import { Html } from '@react-three/drei'
import { Snowflake, TriangleAlert } from 'lucide-react'

const M = (mm: number) => mm / 1000

/** Équipements métier visibles : groupe froid et signalisation ADR. */
export function VehicleEquipmentMarkers({
  length, width, height, hasReefer, isAdr, showLabels, mountReeferOnCabRoof = false,
}: {
  length: number; width: number; height: number
  hasReefer: boolean; isAdr: boolean; showLabels: boolean
  /** Fourgon et porteur : groupe froid horizontal, posé au-dessus de la cabine. */
  mountReeferOnCabRoof?: boolean
}) {
  if (!hasReefer && !isAdr) return null
  const reeferWidth = Math.min(width * 0.75, 1850)
  const reeferHeight = Math.min(height * 0.36, 980)
  const cabRoofHeight = height * 0.88
  // Après rotation, l'ancienne profondeur devient la hauteur du groupe. On
  // l'ajuste au vide disponible : le groupe reste au-dessus de la cabine,
  // sans jamais passer au-dessus du toit du caisson.
  const reeferDepth = mountReeferOnCabRoof
    ? Math.min(440, Math.max(1, height - cabRoofHeight - 30))
    : 440
  const reeferPosition: [number, number, number] = mountReeferOnCabRoof
    // La face arrière du groupe (après rotation) est à x = 0 : il est collé
    // au caisson. Son sommet est exactement au niveau du toit du caisson.
    ? [-M(reeferHeight / 2), M(height - reeferDepth / 2), M(width / 2)]
    : [-M(reeferDepth / 2 + 10), M(height * 0.72), M(width / 2)]

  return (
    <group>
      {hasReefer && (
        <group position={reeferPosition} rotation={mountReeferOnCabRoof ? [0, 0, Math.PI / 2] : undefined}>
          <mesh castShadow receiveShadow><boxGeometry args={[M(reeferDepth), M(reeferHeight), M(reeferWidth)]} /><meshPhysicalMaterial color="#f8fafc" metalness={0.35} roughness={0.2} clearcoat={0.9} /></mesh>
          <mesh position={[-M(reeferDepth / 2 + 2), 0, 0]}><boxGeometry args={[M(8), M(reeferHeight * 0.65), M(reeferWidth * 0.75)]} /><meshStandardMaterial color="#0f172a" metalness={0.8} /></mesh>
          <mesh position={[-M(reeferDepth / 2 + 4), M(reeferHeight * 0.38), 0]}><boxGeometry args={[M(10), M(reeferHeight * 0.12), M(reeferWidth * 0.88)]} /><meshStandardMaterial color="#0284c7" emissive="#0369a1" emissiveIntensity={0.5} /></mesh>
          {[-reeferWidth * 0.22, reeferWidth * 0.22].map((offset, index) => (
            <mesh key={index} position={[0, M(reeferHeight / 2 + 4), M(offset)]}><cylinderGeometry args={[M(reeferDepth * 0.38), M(reeferDepth * 0.38), M(12), 24]} /><meshStandardMaterial color="#334155" metalness={0.7} /></mesh>
          ))}
          {showLabels && <Html position={[-M(reeferDepth / 2 + 15), 0, 0]} center distanceFactor={7} occlude={false}><div className="flex items-center gap-1.5 rounded-full border border-cyan-400/80 bg-slate-950/90 px-3 py-1 shadow-lg backdrop-blur-md"><Snowflake className="h-4 w-4 text-cyan-400 animate-pulse" /><span className="text-[10px] font-black uppercase tracking-wider text-cyan-200">FRIGO</span></div></Html>}
        </group>
      )}
      {isAdr && (
        <group>
          <group position={[M(length + 6), M(height * 0.28), M(width * 0.78)]}>
            <mesh rotation={[0, Math.PI / 2, 0]} castShadow><boxGeometry args={[M(400), M(300), M(10)]} /><meshStandardMaterial color="#f97316" emissive="#ea580c" emissiveIntensity={0.35} /></mesh>
            <mesh rotation={[0, Math.PI / 2, 0]} position={[0, 0, M(-2)]}><boxGeometry args={[M(420), M(320), M(6)]} /><meshStandardMaterial color="#0f172a" metalness={0.8} /></mesh>
            <mesh rotation={[0, Math.PI / 2, 0]} position={[0, 0, M(6)]}><boxGeometry args={[M(390), M(8), M(4)]} /><meshStandardMaterial color="#000000" /></mesh>
            {showLabels && <Html position={[M(12), 0, 0]} center distanceFactor={7} occlude={false}><div className="flex items-center gap-1.5 rounded-md border border-amber-400/90 bg-amber-950/95 px-2.5 py-1 shadow-lg backdrop-blur-md"><TriangleAlert className="h-3.5 w-3.5 text-amber-400" /><span className="text-[9px] font-black uppercase tracking-wider text-amber-200">ADR</span></div></Html>}
          </group>
          {[width + 8, -8].map((z, index) => (
            <group key={index} position={[M(length * 0.86), M(height * 0.52), M(z)]} rotation={[0, 0, Math.PI / 4]}>
              <mesh castShadow><boxGeometry args={[M(240), M(240), M(8)]} /><meshStandardMaterial color="#dc2626" emissive="#991b1b" emissiveIntensity={0.3} /></mesh>
              {showLabels && <Html position={[0, 0, M(8)]} center distanceFactor={7} occlude={false}><div className="flex items-center justify-center rounded-full border border-amber-400/90 bg-amber-950/95 p-1 shadow-md backdrop-blur-md"><TriangleAlert className="h-3.5 w-3.5 text-amber-400" /></div></Html>}
            </group>
          ))}
        </group>
      )}
    </group>
  )
}
